"use client";

import { useState, useEffect } from "react";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NAV_ITEMS } from "@/lib/nav.config";

export default function SiteNav() {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);

  // Prevent scrolling when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  return (
    <header className="sticky top-0 z-50 border-b border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-950/80 backdrop-blur-md transition-colors">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        
        {/* LEFT: Logo */}
        {/* <Link href="/" className="flex items-center gap-3 group">
          <div className="w-10 h-10 rounded-full bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 flex items-center justify-center font-serif font-bold text-lg group-hover:scale-105 transition-transform">
            RR
          </div>
          <span className="font-serif text-lg font-medium text-neutral-900 dark:text-white">Reza Riyadhi</span>
        </Link> */}
        <ThemeToggle className="visible" />

        {/* RIGHT: Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          <nav className="flex items-center gap-8">
            {NAV_ITEMS.map((item) => {
              const isActive =
                item.href === "/"
                  ? pathname === "/"
                  : pathname.startsWith(item.href);

              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`text-sm font-medium transition-colors relative ${
                    isActive
                      ? "text-neutral-900 dark:text-white after:content-[''] after:absolute after:left-0 after:-bottom-1 after:w-full after:h-px after:bg-neutral-900 dark:after:bg-neutral-100"
                      : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Hamburger Button (Mobile) */}
        <button
          className={`md:hidden relative w-10 h-10 flex flex-col justify-center items-center gap-1.5 focus:outline-none transition-all duration-300 ${
            isOpen ? "z-[110]" : "z-50"
          }`}
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          <span
            className={`w-6 h-0.5 bg-neutral-900 dark:bg-white transition-all duration-300 ${
              isOpen ? "rotate-45 translate-y-2" : ""
            }`}
          />
          <span
            className={`w-6 h-0.5 bg-neutral-900 dark:bg-white transition-all duration-300 ${
              isOpen ? "opacity-0" : ""
            }`}
          />
          <span
            className={`w-6 h-0.5 bg-neutral-900 dark:bg-white transition-all duration-300 ${
              isOpen ? "-rotate-45 -translate-y-2" : ""
            }`}
          />
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-[100] w-full h-screen h-[100dvh] bg-white dark:bg-neutral-950 flex flex-col items-center justify-center transition-all duration-500 ease-in-out md:hidden ${
          isOpen 
            ? "opacity-100 translate-y-0" 
            : "opacity-0 -translate-y-full pointer-events-none"
        }`}
      >
        <nav className="flex flex-col items-center gap-10 text-center">
          {NAV_ITEMS.map((item, index) => {
            const isActive =
              item.href === "/"
                ? pathname === "/"
                : pathname.startsWith(item.href);

            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setIsOpen(false)}
                style={{ transitionDelay: isOpen ? `${index * 50}ms` : '0ms' }}
                className={`text-3xl font-serif font-medium transition-all duration-300 ${
                  isOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                } ${
                  isActive
                    ? "text-neutral-900 dark:text-white"
                    : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Additional info or close text if desired */}
        <div className={`absolute bottom-12 transition-all duration-700 delay-300 ${isOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="text-xs uppercase tracking-widest text-neutral-400 dark:text-neutral-500 font-medium">
            Menu
          </p>
        </div>
      </div>
    </header>
  );
}
